import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface FeatureFlag {
  id: string
  name: string
  key: string
  description: string
  enabled: boolean
  environment: string
  rollout_percentage: number
  conditions: Record<string, any>[]
  created_at: string
  updated_at: string
}

export default function FeatureFlagsPage() {
  const [flags, setFlags] = useState<FeatureFlag[]>([])
  const [statistics, setStatistics] = useState<{total: number; enabled: number; environments: Record<string, number>} | null>(null)
  const [selectedEnv, setSelectedEnv] = useState<string>('all')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [flagsRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/feature-flags'),
      fetch(API_BASE + '/feature-flags/statistics')
    ])
    if (flagsRes.ok) setFlags((await flagsRes.json()).flags || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const toggleFlag = async (id: string, enabled: boolean) => {
    const res = await fetch(API_BASE + '/feature-flags/' + id + '/toggle', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled })
    })
    if (res.ok) fetchData()
  }

  const getEnvColor = (env: string) => {
    switch (env) {
      case 'production': return 'bg-red-100 text-red-800'
      case 'staging': return 'bg-yellow-100 text-yellow-800'
      case 'development': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const filteredFlags = selectedEnv === 'all'
    ? flags
    : flags.filter(f => f.environment === selectedEnv)

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Feature Flags Management</h1>

        {statistics && (
          <div className="grid grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Total Flags</div>
              <div className="text-3xl font-bold">{statistics.total}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Enabled</div>
              <div className="text-3xl font-bold text-green-600">{statistics.enabled}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Disabled</div>
              <div className="text-3xl font-bold text-gray-400">{statistics.total - statistics.enabled}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Environments</div>
              <div className="text-3xl font-bold">{Object.keys(statistics.environments || {}).length}</div>
            </div>
          </div>
        )}

        <div className="flex gap-2 mb-6">
          {['all', 'production', 'staging', 'development'].map(env => (
            <button
              key={env}
              onClick={() => setSelectedEnv(env)}
              className={`px-4 py-2 rounded-lg capitalize ${
                selectedEnv === env
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border hover:bg-gray-50'
              }`}
            >
              {env}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-lg shadow">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Flag</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Key</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Environment</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rollout</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredFlags.map(flag => (
                <tr key={flag.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium">{flag.name}</div>
                    <div className="text-sm text-gray-500">{flag.description}</div>
                  </td>
                  <td className="px-6 py-4">
                    <code className="px-2 py-1 bg-gray-100 rounded text-sm">{flag.key}</code>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${getEnvColor(flag.environment)}`}>
                      {flag.environment}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-600 rounded-full"
                          style={{ width: `${flag.rollout_percentage}%` }}
                        />
                      </div>
                      <span className="text-sm">{flag.rollout_percentage}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      flag.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {flag.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => toggleFlag(flag.id, !flag.enabled)}
                      className={`px-3 py-1 rounded text-sm ${
                        flag.enabled
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {flag.enabled ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  )
}
